<template>
  <div class="p-6 lg:p-10 pt-0">
    <div class="block lg:flex items-start">
      <div
        class="hidden lg:block w-3/12 bg-white top-0 bottom-0 border-r border-gray-300 min-h-[100vh] max-h-[100vh] h-full sticky pb-20 pt-8 px-16 overflow-y-auto custom-scroll"
      >
        <div class="mb-10">
          <h2 class="mb-1 text-xl md:text-2xl font-semibold pt-4">{{ action == 'onboard' ? 'Create Your Profile' : 'Profile Information' }}</h2>
          <a
            v-if="action != 'onboard'"
            href="/dashboard"
            class="text-sm text-blue-700 font-semibold inline-flex gap-x-2 items-center"
            @click.prevent="$router.go(-1)"
          >
            <img src="@/assets/icons/arrowbackline.svg" alt="" />
            <span>Back to Dashboard</span>
          </a>
        </div>

        <step-item v-for="(step, i) in steps" :key="i" :title="step.title" :details="details" :index="i" @click="stepIndex = i" />
      </div>

      <div class="flex-1 pt-10 px-16">
        <about
          v-if="stepIndex == 0"
          :details="details"
          :countries="countries"
          :timezones="timezones"
          :resume-url="resumeUrl"
          :avatar-url="avatarUrl"
          @done="updateDetails"
          @photo-uploaded="uploadPhoto"
        />

        <experience v-else-if="stepIndex == 1" :details="details" />

        <qualifications v-else-if="stepIndex == 2" :details="details" />

        <skills
          :skill-list="skillList"
          :details="details"
          @search="getSkills"
          @update="($event) => (newSkills = $event)"
          v-else-if="stepIndex == 3"
        />

        <work-preference
          :details="details"
          :enums="enums"
          :categories="companyCategories"
          :job-categories="jobCategories"
          v-else-if="stepIndex == 4"
        />

        <salary :details="details" :enums="enums" v-else-if="stepIndex == 5" />

        <projects :details="details" v-else-if="stepIndex == 6" />

        <div class="mt-10 block md:flex items-center" :class="[action == 'onboard' ? 'justify-between' : 'justify-end']">
          <div></div>
          <!-- <router-link to="/dashboard" class="font-medium text-lg underline inline-block mb-4 md:mb-0" v-show="action == 'onboard'"
            >Skip to dashboard</router-link
          > -->

          <div class="flex items-center justify-between md:justify-start gap-x-3">
            <Button
              data-test="login-button"
              label="Previous"
              color="primary"
              :outline="true"
              type="button"
              class="px-8 rounded-xl"
              :disabled="stepIndex == 0"
              @click="prev"
            />

            <Button data-test="login-button" label="Continue" color="primary" type="button" class="px-8 rounded-xl" @click="proceed" />
          </div>
        </div>
      </div>
    </div>
  </div>

  <LoadingOverlay :is-loading="loading" :text="loadingTxt" />
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import StepItem from '@/components/Onboarding/StepItem.vue';
import About from '@/components/Onboarding/About.vue';
import Button from '@/components/Button.vue';
import Qualifications from '@/components/Onboarding/Qualifications.vue';
import Experience from '@/components/Onboarding/Experience.vue';
import Skills from '@/components/Onboarding/Skills.vue';
import { useStore } from 'vuex';
import CountryList from 'country-list-with-dial-code-and-flag';
import { timezones } from '@/data/timezones';
import WorkPreference from '@/components/Onboarding/WorkPreference.vue';
import Salary from '@/components/Onboarding/Salary.vue';
import Projects from '@/components/Onboarding/Projects.vue';
import { alert, errorMessage } from '@/utils/helper';
import { useRouter } from 'vue-router';
import LoadingOverlay from '@/components/Loading.vue';

const props = defineProps({
  action: {
    type: String,
    default: 'onboard',
  },
  previousDetails: {
    type: Object,
    required: false,
  },
  resumeUrl: {
    type: String,
    required: false,
  },
  avatarUrl: {
    type: String,
    required: false,
  },
});

const store = useStore();
const router = useRouter();
const loading = ref(false);
const loadingTxt = ref('');
const stepIndex = ref(0);
const steps = ref([
  { title: 'About you' },
  { title: 'Experience' },
  { title: 'Qualifications' },
  { title: 'Skills' },
  { title: 'Work preferences' },
  { title: 'Salary & Billing' },
  { title: 'Projects' },
]);

const details = ref({
  candidate_name: '',
  candidate_email: '',
  candidate_phone: '',
  candidate_address: '',
  candidate_language: '',
  candidate_spoken_languages: [],
  candidate_honors_and_awards: [],
  candidate_courses_and_certifications: [],
  positions: [],
  education_qualifications: [],
  role_title: '',
  social_links: {
    linkedin: '',
    github: '',
    dribbble: '',
    stackoverflow: '',
    behance: '',
    glassdoor: '',
    website: '',
  },
  years_of_experience: '',
  skills: [],
  gender: '',
  availability_status: '',
  projects: [],
  city: '',
  state: '',
  country: '',
  timezone: '',
  desired_salary: '',
  desired_salary_currency: '',
  professional_title: '',
  dob: '',
  company_categories: [],
  next_role_perks: [],
  job_types: [],
  work_type: [],
});
const skillList = ref([]);
const skillListDefault = ref([]);
const newSkills = ref([]);
const enums = ref(null);
const companyCategories = ref([]);
const jobCategories = ref([]);

const countries = computed(() => {
  return CountryList.getAll();
});

const prev = () => {
  if (stepIndex.value > 0) {
    stepIndex.value -= 1;
  }
};

const proceed = () => {
  if (stepIndex.value < 6) {
    if (stepIndex.value == 0) {
      const { candidate_name, candidate_phone, candidate_address, dob, gender, years_of_experience, city, state, country, timezone, social_links } =
        details.value;
      if (!candidate_name || !candidate_phone || !dob || !years_of_experience || !city || !state || !country || !timezone || !social_links.linkedin)
        return alert('Form data incomplete. Try again', 'error');
    }
    stepIndex.value += 1;
  } else {
    completeOnboard();
  }
};

const updateDetails = async ({ parsedResume, resumeFile }) => {
  //save resume file to backend
  await saveResumeFile(resumeFile);
  //update parsed resume details
  details.value = { ...details.value, ...parsedResume };
  console.log(details.value, 'lllllllllllllllll')
};

const saveResumeFile = async (resumeFile) => {
  try {
    loading.value = true;
    const data = new FormData();
    data.append('resume', resumeFile);
    await store.dispatch('auth/onboardResume', data);
  } catch (error) {
    alert(errorMessage(error));
  } finally {
    loading.value = false;
  }
};

const uploadPhoto = async (photo) => {
  try {
    loading.value = true;
    const data = new FormData();
    data.append('profile_picture', photo);
    const { message } = await store.dispatch('auth/saveProfilePic', data);
    alert('Profile photo updated', 'success');
  } catch (error) {
    alert(errorMessage(error), 'error');
  } finally {
    loading.value = false;
  }
};

const completeOnboard = async () => {
  try {
    loadingTxt.value = 'Setting up your profile..';
    loading.value = true;

    const {
      candidate_name,
      candidate_email,
      candidate_phone,
      candidate_address,
      candidate_language,
      candidate_spoken_languages,
      candidate_honors_and_awards,
      candidate_courses_and_certifications,
      positions,
      education_qualifications,
      ...rest
    } = details.value;

    const { message } = await store.dispatch('auth/onboardProfile', {
      ...rest,
      parsed_resume: {
        candidate_name,
        candidate_email,
        candidate_phone,
        candidate_address,
        candidate_language,
        candidate_spoken_languages,
        candidate_honors_and_awards,
        candidate_courses_and_certifications,
        positions,
        education_qualifications,
      },
    });

    if (newSkills.value.length) {
      await saveSkills();
    }

    alert(message || 'Profile updated successfully', 'success');

    if (props.action == 'onboard') router.push('/dashboard');
  } catch (error) {
    alert(errorMessage(error), 'error');
  } finally {
    loading.value = false;
    loadingTxt.value = '';
  }
};

const saveSkills = async () => {
  try {
    loading.value = true;
    await store.dispatch('global/saveSkills', { names: newSkills.value });
  } catch (error) {
    alert(errorMessage(error), 'error');
  } finally {
    loading.value = false;
  }
};

const getSkills = async (query = '') => {
  try {
    // const skills = await store.dispatch('global/getSkillsListSharpApi', `?per_page=100`);
    const skills = await store.dispatch('global/getSkills', query);
    skillListDefault.value = [...skills];
    skillList.value = skills.map((item) => item.name);
  } catch (error) {}
};

const pageSetup = async () => {
  try {
    loading.value = true;
    await getSkills();
    enums.value = await store.dispatch('global/getEnums');
    companyCategories.value = await store.dispatch('global/getCompanyCategory');
    jobCategories.value = await store.dispatch('global/getJobCategories');
  } catch (error) {
  } finally {
    loading.value = false;
  }
};

onMounted(async () => {
  await Promise.all([pageSetup()]);

  if (props.action == 'profile') {
    details.value = { ...details.value, ...props.previousDetails };
  }
});
</script>
